/**
 * Curated list of skateboarding tags for spot categorization.
 * Tags are organized by category for autocomplete suggestions.
 * 
 * Rules:
 * - All tags are lowercase
 * - Multi-word tags use hyphens (no spaces)
 * - No special characters except hyphens
 * - No # prefix (added in UI only)
 */

export interface TagCategory {
  name: string;
  description: string;
  tags: string[];
}

// Feature tags - obstacles and terrain types
export const featureTags: TagCategory = {
  name: 'Features',
  description: 'Obstacles and terrain',
  tags: [
    // Ledges & Manual Pads
    'ledge',
    'manual-pad',
    'manny-pad',
    'curb',
    'planter',
    'bench',
    'picnic-table',
    'hubba',
    'hubba-ledge',
    'slappy-curb',
    'waxed',
    
    // Rails
    'rail',
    'handrail',
    'flatrail',
    'flat-bar',
    'kinked-rail',
    'kink',
    'rainbow-rail',
    'down-rail',
    'double-kinked',
    'curved-rail',
    'pole-jam',
    
    // Stairs & Gaps
    'stairs',
    'stair-set',
    'gap',
    'euro-gap',
    'hip',
    'drop',
    'loading-dock',
    'bump-to-bar',
    'out-ledge',
    
    // Transition
    'quarter',
    'quarter-pipe',
    'halfpipe',
    'half-pipe',
    'miniramp',
    'mini-ramp',
    'vert',
    'bowl',
    'pool',
    'cradle',
    'spine',
    'channel',
    'extension',
    'coping',
    'roll-in',
    'fly-out',
    'tombstone',
    
    // Banks & Ramps
    'bank',
    'bank-to-wall',
    'kicker',
    'launch',
    'pyramid',
    'volcano',
    'fun-box',
    'funbox',
    'a-frame',
    
    // Flow Features
    'snake-run',
    'pump-track',
    'flow-section',
    'lines',
    
    // Walls & Vertical
    'wallride',
    'wall-ride',
    'wall',
    'ditch',
    'full-pipe',
    
    // Flatground
    'flatground',
    'flat',
    'smooth-ground',
  ],
};

// Surface tags - materials and textures
export const surfaceTags: TagCategory = {
  name: 'Surfaces',
  description: 'Materials and textures',
  tags: [
    'concrete',
    'marble',
    'granite',
    'brick',
    'metal',
    'steel',
    'wood',
    'asphalt',
    'tile',
    'terrazzo',
    'stone',
    'painted',
    'sealed',
    'polished',
    'smooth',
    'rough',
    'crusty',
    'slippery',
    'grippy',
    'cracked',
    'pebbled',
  ],
};

// Size tags - dimensions and scale
export const sizeTags: TagCategory = {
  name: 'Sizes',
  description: 'Dimensions and scale',
  tags: [
    'small',
    'medium',
    'large',
    'massive',
    'huge',
    'tiny',
    'mellow',
    'steep',
    'low',
    'high',
    'long',
    'short',
    'wide',
    'narrow',
    'deep',
    'shallow',
  ],
};

// Stair count tags
export const stairTags: TagCategory = {
  name: 'Stair Counts',
  description: 'Number of stairs',
  tags: [
    '2-stair',
    '3-stair',
    '4-stair',
    '5-stair',
    '6-stair',
    '7-stair',
    '8-stair',
    '9-stair',
    '10-stair',
    '11-stair',
    '12-stair',
    '10-plus',
    'double-set',
    'triple-set',
  ],
};

// Vibe & condition tags
export const vibeTags: TagCategory = {
  name: 'Vibes',
  description: 'Atmosphere and condition',
  tags: [
    // Fame & History
    'famous',
    'classic',
    'legendary',
    'iconic',
    'hidden-gem',
    'underrated',
    'local-secret',
    
    // Skill Level
    'beginner-friendly',
    'beginner',
    'intermediate',
    'advanced',
    'pro-spot',
    'gnarly',
    'scary',
    
    // Atmosphere
    'chill',
    'mellow-vibes',
    'locals-only',
    'welcoming',
    'busy',
    'empty',
    'crowded',
    
    // Time & Lighting
    'night-spot',
    'night-lights',
    'lit',
    'well-lit',
    'dark',
    'daytime-only',
    '24-hours',
    
    // Weather Protection
    'covered',
    'shaded',
    'indoor',
    'outdoor',
    'rain-protected',
    'under-bridge',
    
    // Security & Access
    'security',
    'guards',
    'bust-factor',
    'chill-security',
    'no-security',
    'skate-stopped',
    'knobbed',
    'capped',
    'clipped',
    
    // Condition
    'well-maintained',
    'clean',
    'pristine',
    'run-down',
    'abandoned',
    'demolished',
    'renovated',
    'new',
    'old-school',
  ],
};

// Location context tags
export const locationTags: TagCategory = {
  name: 'Location',
  description: 'Location context',
  tags: [
    'downtown',
    'city-center',
    'suburban',
    'industrial',
    'residential',
    'campus',
    'university',
    'school',
    'waterfront',
    'beach',
    'riverside',
    'rooftop',
    'underground',
    'basement',
    'parking-garage',
    'parking-lot',
    'business-district',
    'shopping-center',
    'mall',
    'train-station',
    'metro',
    'museum',
    'library',
    'church',
    'government',
    'courthouse',
    'hospital',
    'airport',
  ],
};

// Trick-specific tags
export const trickTags: TagCategory = {
  name: 'Tricks',
  description: 'Good for specific tricks',
  tags: [
    'grind-spot',
    'slide-spot',
    'manual-spot',
    'flip-tricks',
    'tech-spot',
    'tech-ledge',
    'power-moves',
    'transition-tricks',
    'airs',
    'transfers',
    'lines',
    'speed',
    'flow',
  ],
};

// All tag categories
export const allTagCategories: TagCategory[] = [
  featureTags,
  surfaceTags,
  sizeTags,
  stairTags,
  vibeTags,
  locationTags,
  trickTags,
];

// Flat list of all curated tags
export const allCuratedTags: string[] = allTagCategories.flatMap(cat => cat.tags);

// Set for O(1) lookup
export const curatedTagSet = new Set(allCuratedTags);

/**
 * Check if a tag is in the curated list
 */
export function isCuratedTag(tag: string): boolean {
  return curatedTagSet.has(tag.toLowerCase());
}

/**
 * Normalize a tag: lowercase, replace spaces with hyphens, remove special chars
 */
export function normalizeTag(tag: string): string {
  return tag
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')           // spaces to hyphens
    .replace(/[^a-z0-9-]/g, '')     // remove special chars
    .replace(/-+/g, '-')            // collapse multiple hyphens
    .replace(/^-|-$/g, '');         // trim leading/trailing hyphens
}

/**
 * Search tags by prefix for autocomplete
 */
export function searchTags(query: string, limit: number = 10): string[] {
  const normalized = normalizeTag(query);
  if (!normalized) return [];
  
  const results: string[] = [];
  
  // First, exact prefix matches
  for (const tag of allCuratedTags) {
    if (tag.startsWith(normalized)) {
      results.push(tag);
      if (results.length >= limit) return results;
    }
  }
  
  // Then, contains matches
  for (const tag of allCuratedTags) {
    if (tag.includes(normalized) && !tag.startsWith(normalized)) {
      results.push(tag);
      if (results.length >= limit) return results;
    }
  }
  
  return results;
}

/**
 * Get suggested tags by category
 */
export function getTagsByCategory(categoryName: string): string[] {
  const category = allTagCategories.find(
    cat => cat.name.toLowerCase() === categoryName.toLowerCase()
  );
  return category?.tags ?? [];
}

/**
 * Validate tags array - returns valid tags and warnings for non-curated tags
 */
export function validateTags(tags: string[]): {
  valid: string[];
  warnings: string[];
} {
  const valid: string[] = [];
  const warnings: string[] = [];
  
  for (const tag of tags) {
    const normalized = normalizeTag(tag);
    if (normalized) {
      valid.push(normalized);
      if (!isCuratedTag(normalized)) {
        warnings.push(normalized);
      }
    }
  }
  
  return { valid, warnings };
}

// Spot categories (for the category dropdown)
export const spotCategories = [
  { value: 'street', label: 'Street', description: 'Street spots - stairs, rails, ledges' },
  { value: 'park', label: 'Park', description: 'Skateparks - public or private' },
  { value: 'diy', label: 'DIY', description: 'DIY/homemade spots' },
  { value: 'plaza', label: 'Plaza', description: 'Public plazas with skate-friendly architecture' },
] as const;

export type SpotCategory = typeof spotCategories[number]['value'];

/**
 * Shelter / weather-protection level of a spot. Skaters distinguish between a
 * fully-exposed street spot and a roofed/indoor one, because rain kills the
 * former but not the latter — this is what stops weather info from being
 * misleading ("it's raining, but this spot is covered / indoors").
 *
 * Stored on the spot event as a `['shelter', value]` tag plus a matching
 * `['t', value]` tag for relay-level filtering (indoor/covered/partial).
 * 'outdoor' is the default and adds no `t` tag (avoids tagging every spot).
 */
export const shelterOptions = [
  { value: 'outdoor', emoji: '☀️', label: 'Outdoor', description: 'Fully exposed — rain stops play' },
  { value: 'partial', emoji: '🌤️', label: 'Partly covered', description: 'Some obstacles stay dry' },
  { value: 'covered', emoji: '🏠', label: 'Covered / roofed', description: 'Outdoor but under a roof' },
  { value: 'indoor', emoji: '🏢', label: 'Indoor', description: 'Fully enclosed — skate rain or shine' },
] as const;

export type ShelterType = typeof shelterOptions[number]['value'];

/** The default shelter value when the user doesn't pick one. */
export const DEFAULT_SHELTER: ShelterType = 'outdoor';

/** Shelter values that get a queryable `t` tag (outdoor is the implicit default). */
export const SHELTERED_VALUES: ShelterType[] = ['partial', 'covered', 'indoor'];

// Max tags allowed per spot
export const MAX_TAGS = 20;
